import { Flex } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../../utils/cn.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../../../redux/leads.types.ts"
import {
  FlameIcon,
  FunnelIcon,
  ClockIcon
} from "../kpi/lead-kpi-icons.tsx"
import {
  clearedActiveFilters,
  narrowsAnything
} from "../../../redux/leads.constants.ts"
import {
  LEAD_SMART_FILTERS,
  type LeadSmartFilter,
  isSmartFilterActive,
  isSmartFilterUsable
} from "./lead-smart-filters.ts"
import { activeFilterChips } from "./active-filter-summary.ts"

const SparkIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z" />
  </svg>
)

const ArrowDownIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M12 5v14M19 12l-7 7-7-7" />
  </svg>
)

const CloseIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const CHIP_ICONS = {
  spark: SparkIcon,
  flame: FlameIcon,
  funnel: FunnelIcon,
  clock: ClockIcon,
  "arrow-down": ArrowDownIcon
} as const

interface LeadSmartFiltersProps {
  filters: LeadsFilters
  counts?: Record<string, number> | null
  userId?: string | null
  disabled?: boolean
  /** Resolves a stored id to the label the user picked it by. */
  filterOptions?: Partial<LeadsFilterOptions>
  onFiltersChange: (filters: Partial<LeadsFilters>) => void
}

function SmartChip({
  preset,
  active,
  count,
  disabled,
  onClick
}: {
  preset: LeadSmartFilter
  active: boolean
  count?: number
  disabled?: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const Icon = CHIP_ICONS[preset.icon]

  return (
    <button
      type="button"
      data-testid={`smart-chip-${preset.key}`}
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border bg-white px-3.5 text-[13px] font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-60",
        active
          ? "border-blue-500 bg-blue-50 text-blue-600"
          : "border-slate-200 text-slate-700 hover:border-slate-300"
      )}
    >
      <span style={{ color: preset.tint, lineHeight: 0 }}>
        <Icon />
      </span>
      {t(preset.labelKey)}
      {count !== undefined && (
        // The design tints the count to the chip's own colour rather than
        // boxing it in a neutral badge.
        <span className={"font-semibold"} style={{ color: preset.tint }}>
          {count}
        </span>
      )}
    </button>
  )
}

export function LeadSmartFilters({
  filters,
  counts,
  userId,
  disabled,
  filterOptions,
  onFiltersChange
}: LeadSmartFiltersProps) {
  const { t } = useTranslation()
  const context = { userId }

  return (
    <Flex
      wrap="wrap"
      gap="small"
      align="center"
      className={"leads-smart-filters px-4 py-3"}
    >
      {/* Title and strapline sit on one line, as the design has them. */}
      <Flex align={"center"} gap={8} className={"mr-3"}>
        <span style={{ color: "#8B5CF6", lineHeight: 0 }}>
          <SparkIcon width={16} height={16} />
        </span>
        <span className={"text-[14px] font-semibold text-slate-900"}>
          {t("leadsTable.smartFilters.title")}
        </span>
        <span className={"text-[12px] text-slate-500"}>
          {t("leadsTable.smartFilters.strapline")}
        </span>
      </Flex>
      {LEAD_SMART_FILTERS.map((preset) => (
        <SmartChip
          key={preset.key}
          preset={preset}
          // A preset that names the signed-in user cannot be applied without
          // one, so it says so rather than quietly filtering nothing.
          disabled={disabled || !isSmartFilterUsable(preset, context)}
          active={isSmartFilterActive(preset, filters, context)}
          count={preset.statsKey ? counts?.[preset.statsKey] : undefined}
          onClick={() => onFiltersChange(preset.build(context))}
        />
      ))}
      {activeFilterChips(filters, t, filterOptions).map(({ key, label, cleared }) => (
        <span
          key={key}
          data-testid={"active-filter-chip"}
          className={
            "inline-flex h-9 items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 text-[13px] font-medium text-blue-700"
          }
        >
          {label}
          <button
            type="button"
            disabled={disabled}
            aria-label={t("leadsTable.smartFilters.remove", { filter: label })}
            onClick={() => onFiltersChange(cleared)}
            className={"cursor-pointer leading-none disabled:cursor-not-allowed"}
          >
            <CloseIcon width={12} height={12} />
          </button>
        </span>
      ))}
      {narrowsAnything(filters) && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onFiltersChange(clearedActiveFilters())}
          className={cn(
            "inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 text-[13px] font-medium text-slate-600 transition-colors",
            "hover:border-slate-400 hover:text-slate-900",
            "disabled:cursor-not-allowed disabled:opacity-60"
          )}
        >
          <CloseIcon />
          {t("leadsTable.smartFilters.clear")}
        </button>
      )}
    </Flex>
  )
}
